"use client";

/**
 * Staff → Admissions Review → Awaiting applicant.
 *
 * Where staff work what the committee has asked an applicant for: the email to
 * send them (drafted from the committee's own words), then — when the applicant
 * replies — the upload that puts their documents and answer in front of the
 * committee. Sending it on emails the member who asked and everyone yet to vote.
 *
 * The one place staff write in admissions, and it decides nothing: it carries
 * the applicant's answer to the committee, who still evaluate and vote. The
 * backend enforces staff-only (`require_admissions_staff`).
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Check, Copy, Inbox, Mail, Paperclip, Upload } from "lucide-react";
import {
  resolveInfoRequest,
  type InfoRequest,
  type StaffInfoRequestGroup,
} from "@/lib/admissions-committee/api";

const label = "text-[10px] font-black uppercase tracking-widest text-gray-500";
const chip = "text-[9.5px] font-bold tracking-wide rounded px-1.5 py-0.5";

const ACCEPT =
  ".pdf,.doc,.docx,.rtf,.ppt,.pptx,.xls,.xlsx,.csv,.tsv,.png,.jpg,.jpeg,.gif,.webp,.bmp,.txt,.md";
const MAX_MB = 25;

const daysSince = (iso?: string | null) =>
  iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000)) : 0;
const dateOf = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "";

const STATUS_WORDS: Record<string, string> = {
  open: "Waiting on the applicant",
  answered: "Sent to the committee",
  no_response: "Closed — no response",
};

export function Steps() {
  return (
    <ol className="grid gap-2 rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-[12.5px] leading-snug text-amber-950 sm:grid-cols-2 lg:grid-cols-4">
      <li>
        <span className="font-black">1. Email the applicant.</span> Use the draft below — copy it or open it
        in your mail app, and edit as you like.
      </li>
      <li>
        <span className="font-black">2. Wait for their reply.</span> The application shows as
        &ldquo;Awaiting applicant&rdquo; to the committee meanwhile.
      </li>
      <li>
        <span className="font-black">3. Upload what they sent</span> and/or paste their written answer here,
        then press <span className="font-bold">Send to the committee</span>.
      </li>
      <li>
        <span className="font-black">4. No reply?</span> After a reasonable wait, close it as{" "}
        <span className="font-bold">No response</span> with a note of what you tried.
      </li>
    </ol>
  );
}

function DraftEmail({ group }: { group: StaffInfoRequestGroup }) {
  const [copied, setCopied] = useState(false);
  if (!group.draft) return null;
  const { subject, body } = group.draft;
  const to = group.contact.email;
  const mailto = to
    ? `mailto:${to}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
    : null;
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className={label}>Draft email to the applicant</p>
        <div className="flex gap-1.5">
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard
                ?.writeText(`Subject: ${subject}\n\n${body}`)
                .then(() => {
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                });
            }}
            className="inline-flex items-center gap-1 rounded-lg bg-gray-100 px-2.5 py-1 text-[12px] font-bold text-gray-700 hover:bg-gray-200"
          >
            {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? "Copied" : "Copy"}
          </button>
          {mailto && (
            <a
              href={mailto}
              className="inline-flex items-center gap-1 rounded-lg bg-[#007a8a] px-2.5 py-1 text-[12px] font-bold text-white hover:bg-[#006571]"
            >
              <Mail size={12} /> Open in mail app
            </a>
          )}
        </div>
      </div>
      <p className="mt-2 text-[13px] text-gray-700">
        <span className="font-bold">To:</span>{" "}
        {to ?? <span className="text-[#ae232d]">no email in the application — look them up in myCMT</span>}
      </p>
      <p className="text-[13px] text-gray-700">
        <span className="font-bold">Subject:</span> {subject}
      </p>
      <pre className="mt-2 whitespace-pre-wrap rounded-lg bg-gray-50 p-3 font-sans text-[13px] leading-relaxed text-[#1a1a1a]">
        {body}
      </pre>
    </div>
  );
}

function ResolveForm({ request, onDone }: { request: InfoRequest; onDone: (message: string) => void }) {
  const [files, setFiles] = useState<File[]>([]);
  const [note, setNote] = useState("");
  const [mode, setMode] = useState<"answered" | "no_response">("answered");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const isDocs = request.row === "consider_additional_docs";

  const tooBig = files.find((f) => f.size > MAX_MB * 1024 * 1024);
  const ready =
    !tooBig && (mode === "answered" ? files.length > 0 || note.trim() !== "" : note.trim() !== "");

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await resolveInfoRequest(request.id, mode, note, mode === "answered" ? files : []);
      onDone(
        mode === "answered"
          ? `Sent to the committee${r.documents.length ? ` with ${r.documents.length} file${r.documents.length === 1 ? "" : "s"}` : ""} — ${r.notified} member${r.notified === 1 ? "" : "s"} emailed.`
          : `Closed as no response — ${r.notified} member${r.notified === 1 ? "" : "s"} emailed.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not send it");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 space-y-2 border-t border-gray-100 pt-3">
      <div className="flex gap-1" role="tablist" aria-label="What happened">
        {(
          [
            ["answered", "The applicant replied"],
            ["no_response", "No response"],
          ] as const
        ).map(([value, text]) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={mode === value}
            onClick={() => setMode(value)}
            className={`rounded-lg px-2.5 py-1 text-[12px] font-bold transition ${
              mode === value ? "bg-[#003d45] text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {text}
          </button>
        ))}
      </div>

      {mode === "answered" && (
        <div>
          <input
            ref={input}
            type="file"
            multiple
            accept={ACCEPT}
            aria-label="Documents the applicant sent"
            className="hidden"
            onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
          />
          <button
            type="button"
            onClick={() => input.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-gray-400 px-3 py-2 text-[12.5px] font-bold text-gray-700 hover:bg-gray-50"
          >
            <Paperclip size={13} /> {files.length ? "Change files" : isDocs ? "Attach the documents they sent" : "Attach files (optional)"}
          </button>
          {files.length > 0 && (
            <ul className="mt-1.5 space-y-0.5 text-[12.5px] text-gray-700">
              {files.map((f) => (
                <li key={f.name} className={f.size > MAX_MB * 1024 * 1024 ? "text-[#ae232d]" : ""}>
                  • {f.name} ({(f.size / 1024 / 1024).toFixed(1)} MB)
                  {f.size > MAX_MB * 1024 * 1024 && ` — over ${MAX_MB} MB`}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <textarea
        rows={3}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        aria-label={mode === "answered" ? "The applicant's written reply" : "What was tried"}
        placeholder={
          mode === "answered"
            ? isDocs
              ? "Anything the applicant wrote alongside the documents (optional)"
              : "Paste the applicant's answer to the committee's question"
            : "What was tried — e.g. “Emailed 9 Oct and 16 Oct, no reply.” (required)"
        }
        className="w-full rounded-lg border border-gray-300 px-3 py-2 text-[13px] focus:border-[#007a8a] focus:outline-none focus:ring-2 focus:ring-[#007a8a]/30"
      />

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={!ready || busy}
          onClick={() => void submit()}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#007a8a] px-3 py-1.5 text-[13px] font-bold text-white transition hover:bg-[#006571] disabled:opacity-50"
        >
          <Upload size={13} />
          {busy ? "Sending…" : mode === "answered" ? "Send to the committee" : "Close as no response"}
        </button>
        <p className="text-[12px] text-gray-500">
          Emails the member who asked and everyone on the committee yet to vote.
        </p>
      </div>
      {error && <p className="text-[12px] font-semibold text-[#ae232d]">{error}</p>}
    </div>
  );
}

export default function AwaitingApplicantTab({
  waiting,
  resolved,
  focusReview,
  onChanged,
}: {
  waiting: StaffInfoRequestGroup[];
  resolved: StaffInfoRequestGroup[];
  /** From `?review=` — the application the digest email linked to. */
  focusReview: string | null;
  onChanged: () => void;
}) {
  const [notice, setNotice] = useState<string | null>(null);
  const focused = useMemo(() => focusReview, [focusReview]);

  useEffect(() => {
    if (!focused) return;
    document.getElementById(`awaiting-${focused}`)?.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [focused, waiting.length]);

  return (
    <section className="space-y-4">
      <Steps />
      {notice && <p className="rounded-lg bg-emerald-50 p-2.5 text-[13px] font-semibold text-emerald-800">{notice}</p>}

      <p className={label}>Waiting on the applicant ({waiting.length})</p>
      {waiting.length === 0 && (
        <p className="flex items-center gap-2 rounded-xl border border-dashed border-gray-200 bg-gray-50/60 p-3 text-[13px] text-gray-500">
          <Inbox size={14} /> Nothing outstanding. When a committee member asks for documents or a
          clarification, the applicant appears here with the email to send them.
        </p>
      )}

      {waiting.map((g) => (
        <article
          key={g.review_id}
          id={`awaiting-${g.review_id}`}
          className={`space-y-3 rounded-xl border p-4 ${
            focused === g.review_id ? "border-[#007a8a] ring-2 ring-[#007a8a]/30" : "border-gray-200"
          }`}
        >
          <header>
            <h3 className="text-[16px] font-black text-[#003d45]">{g.applicant_name}</h3>
            <p className="text-[12px] text-gray-500">
              Application #{g.application_id}
              {g.contact.company && ` · ${g.contact.company}`}
              {g.contact.location && ` · ${g.contact.location}`}
              {g.waiting_since && ` · waiting ${daysSince(g.waiting_since)} day${daysSince(g.waiting_since) === 1 ? "" : "s"}`}
            </p>
          </header>

          <DraftEmail group={g} />

          {g.requests
            .filter((r) => r.status === "open")
            .map((r) => (
              <div key={r.id} className="rounded-xl bg-gray-50 p-3">
                <p className={label}>{r.label}</p>
                <p className="mt-1 whitespace-pre-line text-[14px] text-[#1a1a1a]">{r.ask}</p>
                <p className="mt-1 text-[12px] text-gray-500">
                  Asked{r.requested_by && ` by ${r.requested_by}`} on {dateOf(r.requested_at)}
                </p>
                <ResolveForm
                  request={r}
                  onDone={(message) => {
                    setNotice(`${g.applicant_name}: ${message}`);
                    onChanged();
                  }}
                />
              </div>
            ))}
        </article>
      ))}

      {resolved.length > 0 && (
        <>
          <p className={label}>Sent on in the last 30 days ({resolved.length})</p>
          <ul className="space-y-2">
            {resolved.map((g) => (
              <li key={g.review_id} className="rounded-xl border border-gray-200 p-3">
                <p className="font-bold text-[#1a1a1a]">{g.applicant_name}</p>
                {g.requests.map((r) => (
                  <p key={r.id} className="mt-1 text-[12.5px] text-gray-600">
                    <span className={`${chip} mr-1 ${r.status === "answered" ? "bg-emerald-100 text-emerald-800" : "bg-gray-100 text-gray-700"}`}>
                      {STATUS_WORDS[r.status]}
                    </span>
                    {r.label}
                    {r.documents.length > 0 && ` · ${r.documents.length} file${r.documents.length === 1 ? "" : "s"}`}
                    {r.resolved_by && ` · by ${r.resolved_by}`} {dateOf(r.resolved_at)}
                  </p>
                ))}
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
